import "./style.scss";
import {Fragment} from "react";
import {Link} from "react-router-dom";
import Header from "../header/Heade";
import Navbar from "../Navbar/Navbar";
import Footer from "../footer/Footer";
import Seo from "../Seo";
import {GOOGLE_RATING, GOOGLE_REVIEWS, GOOGLE_URL} from "../../googleListing";

// Ohio landing page. Prices are the published Ohio bands on brianleaderautoship.com (lib/lane-data):
// Ohio-Florida open is the owner's ruling: $1,000-1,200 south since 2026-10-15, $1,100-1,300 north; the rest is the rate sheet.
const PHONE = "(567) 666-4696";

const LANES = [
    {route: "Ohio to Florida", note: "Statewide planning range", south: ["$1,000–$1,200", "$1,250–$1,400"],
        back: "Florida to Ohio", north: ["$1,100–$1,300", "$1,450–$1,700"]},
    {route: "Cincinnati to Naples", note: "About 1,100 miles", south: ["$950–$1,300", "$1,500–$1,750"],
        back: "Naples to Cincinnati", north: ["$1,150–$1,300", "$1,600–$1,950"]},
    {route: "Cleveland to Fort Myers", note: "About 1,250 miles", south: ["$950–$1,200", "$1,350–$1,600"],
        back: "Fort Myers to Cleveland", north: ["$1,200–$1,500", "$1,650–$2,000"]},
];

const FAQ = [
    {q: "How much does it cost to ship a car from Ohio to Florida?",
        a: "For a running sedan or SUV on an open carrier, plan on $1,000–$1,200 heading south and $1,100–$1,300 coming back north. Enclosed transport runs $1,250–$1,400 heading south. Your quote depends on the exact pickup and delivery ZIP codes, the vehicle and your dates."},
    {q: "When do I pay?",
        a: "The deposit is charged by card, and your agent tells you when. The balance is paid to the driver after delivery."},
    {q: "Can the price change after I book?",
        a: "Once a carrier is assigned, your price is written into your contract and into the carrier's contract. Nobody, the driver included, can ask for more for the job as described."},
    {q: "Is my car insured during transport?",
        a: "Yes. Cargo cover is included in the price: up to $100,000 on open carriers and up to $1,000,000 on enclosed carriers. After a carrier is assigned you get its insurance certificate, MC and DOT numbers, and the driver's name and phone."},
    {q: "Can I leave things in the car?",
        a: "Up to 100–150 lb of personal items is included. Confirm the weight with your agent, and call the driver to confirm before pickup. Carriers charge about $1 per extra pound."},
];

const jsonLd = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: FAQ.map(({q, a}) => ({"@type": "Question", name: q, acceptedAnswer: {"@type": "Answer", text: a}})),
};

const scrollTop = () => window.scrollTo(0, 0);

const Ohio = () => <div className="ohio-container">
    <Seo title="Ohio Car Shipping Company | 4.9★ on Google | Leader Auto Ship"
         description={`Leader Auto Ship is a licensed auto transport company based in Morrow, Ohio, rated ${GOOGLE_RATING} from ${GOOGLE_REVIEWS} Google reviews. See Ohio to Florida prices and get a free quote.`}
         path="/ohio-car-shipping" jsonLd={jsonLd}/>

    <header>
        <Header/>
        <Navbar/>
    </header>

    <main>
        <section className="ohio-hero">
            <div className="inner">
                <strong className="eyebrow">Based in Morrow, Ohio · MC #1638331 · USDOT #4232283</strong>
                <h1>Ohio car shipping from a company based in Morrow, Ohio</h1>
                <p className="lede">
                    Leader Auto Ship LLC is registered with the FMCSA at 979 Oak Forest Dr, Morrow, Ohio. We ship
                    cars from Cincinnati, Columbus, Cleveland, Dayton, Toledo and Akron to all 50 states, on open
                    or enclosed carriers.
                </p>
                <a className="rating" href={GOOGLE_URL} target="_blank" rel="noopener noreferrer">
                    <span className="stars" aria-hidden="true">★★★★★</span>
                    <span><b>{GOOGLE_RATING}</b> from {GOOGLE_REVIEWS} Google reviews</span>
                    <span className="as-of">October 2026</span>
                </a>
                <div className="actions">
                    <Link to="/get-quote" onClick={scrollTop} className="btn primary">Get a free quote</Link>
                    <a href="tel:+15676664696" className="btn">Call {PHONE}</a>
                </div>
            </div>
        </section>

        <section className="ohio-section">
            <h2>Ohio to Florida car shipping prices</h2>
            <p className="intro">
                Planning ranges for a running sedan or SUV. Your quote depends on the exact ZIP codes, the vehicle
                and your dates. The two directions are priced separately because demand is different each way.
            </p>
            <div className="table-wrap">
                <table>
                    <thead>
                    <tr>
                        <th>Route</th>
                        <th>Open carrier</th>
                        <th>Enclosed carrier</th>
                    </tr>
                    </thead>
                    <tbody>
                    {LANES.map(l => <Fragment key={l.route}>
                        <tr>
                            <td><b>{l.route}</b><small>{l.note}</small></td>
                            <td>{l.south[0]}</td>
                            <td>{l.south[1]}</td>
                        </tr>
                        <tr className="back">
                            <td>{l.back}</td>
                            <td>{l.north[0]}</td>
                            <td>{l.north[1]}</td>
                        </tr>
                    </Fragment>)}
                    </tbody>
                </table>
            </div>
            <p className="note">
                Going south for the winter and back in spring? Ask for both legs in one quote. Shipping from Ohio to
                anywhere else in the country? <Link to="/get-quote" onClick={scrollTop}>Get a quote for your route</Link>.
            </p>
        </section>

        <section className="ohio-section">
            <h2>Why Ohio customers ship with Leader Auto Ship</h2>
            <ul className="facts">
                <li><b>Ohio address on record.</b> FMCSA lists Leader Auto Ship LLC at 979 Oak Forest Dr, Morrow, OH
                    45152, MC #1638331.</li>
                <li><b>{GOOGLE_RATING} stars from {GOOGLE_REVIEWS} reviews.</b> Read them on <a href={GOOGLE_URL}
                    target="_blank" rel="noopener noreferrer">our Google listing</a> or on our <Link to="/reviews"
                    onClick={scrollTop}>reviews page</Link>.</li>
                <li><b>Balance paid at delivery.</b> The deposit goes on your card, and your agent tells you when.
                    The balance is paid to the driver after delivery.</li>
                <li><b>Price written into the contract.</b> Once a carrier is assigned, nobody, the driver included,
                    can ask for more for the job as described.</li>
                <li><b>Cargo cover included.</b> Up to $100,000 on open carriers and up to $1,000,000 enclosed.</li>
                <li><b>More than cars.</b> We also ship <Link to="/rv-transport" onClick={scrollTop}>RVs</Link>, <Link
                    to="/boat-transport" onClick={scrollTop}>boats and jet skis</Link>, and <Link to="/golf-cart-shipping"
                    onClick={scrollTop}>golf carts</Link>.</li>
            </ul>
        </section>

        <section className="ohio-section">
            <h2>How shipping from Ohio works</h2>
            <ol className="steps">
                <li><b>Quote.</b> Tell us the pickup and delivery ZIP codes, the vehicle and your dates.</li>
                <li><b>Carrier assigned.</b> We book a licensed carrier and send you the driver's name and phone,
                    the insurance certificate, MC and DOT numbers.</li>
                <li><b>Pickup.</b> Door to door where the truck can reach. Most carriers run 40-foot-plus trailers,
                    so a large parking lot nearby often works best.</li>
                <li><b>Delivery.</b> You check the car and pay the balance to the driver.</li>
            </ol>
        </section>

        <section className="ohio-section">
            <h2>Ohio car shipping questions</h2>
            <div className="faq">
                {FAQ.map(({q, a}) => <details key={q}>
                    <summary>{q}</summary>
                    <p>{a}</p>
                </details>)}
            </div>
        </section>

        <section className="ohio-cta">
            <h2>Ship your car from Ohio</h2>
            <div className="actions">
                <Link to="/get-quote" onClick={scrollTop} className="btn primary">Get a free quote</Link>
                <a href="tel:+15676664696" className="btn">Call {PHONE}</a>
            </div>
            {/* Required by 49 CFR 371.7(b): advertising must show broker status. Keep this line. */}
            <p className="disclosure">
                Leader Auto Ship LLC is a licensed transportation broker (MC #1638331, USDOT #4232283) and arranges
                transport with licensed, insured carriers.
            </p>
        </section>
    </main>

    <footer>
        <Footer/>
    </footer>
</div>;

export default Ohio;
